import type { InventoryCountColumn, InventoryReconRow, ReconSituation } from "../api/rest";

/**
 * View model for the two-count split (ADR 159). The backend does the pass
 * assignment and per-location reconciliation; these pure helpers only pivot its
 * output into the two shapes the page renders — kept out of the component so they
 * can be asserted directly rather than through the DOM.
 */

/** One level as a row of the per-level summary: its count-1 and count-2 pass
 *  side by side. `c2` is the same object as `c1` for a level walked once. */
export interface LevelSummary {
  aisle: string;
  level: number | null;
  duplicated: boolean;
  c1?: InventoryCountColumn;
  c2?: InventoryCountColumn;
}

/** Keyed on aisle AND level, matching the backend's grouping (ADR 253). Under a
 *  grammar that separates the two, every level of a rack reports the same aisle
 *  — keying on it alone would merge Níveis 2..5 into one row here even though
 *  the backend correctly sent four. */
export function summarizeLevels(columns: InventoryCountColumn[]): LevelSummary[] {
  const bySegment = new Map<string, LevelSummary>();
  for (const c of columns) {
    const key = `${c.aisle} ${c.level ?? ""}`;
    const s = bySegment.get(key) ?? { aisle: c.aisle, level: c.level, duplicated: c.duplicated };
    if (c.count === 1) s.c1 = c;
    else s.c2 = c;
    s.duplicated = c.duplicated;
    bySegment.set(key, s);
  }
  return [...bySegment.values()].sort(
    (a, b) => a.aisle.localeCompare(b.aisle) || (a.level ?? -1) - (b.level ?? -1),
  );
}

/** Agreement class for a location, driving both the pill color and whether a row
 *  is worth the reader's attention. `differ` is the point of the second count —
 *  the reads that a single pass would have gotten wrong or never reached. */
export type SituationTone = "agree" | "differ" | "dup";

const DIFFER: readonly ReconSituation[] = ["one_pass_only", "conflict", "only_count1", "only_count2"];

export function situationTone(s: ReconSituation): SituationTone {
  if (s === "duplicated") return "dup";
  return DIFFER.includes(s) ? "differ" : "agree";
}

/** How many locations the two counts agreed on, differed on, and were shown
 *  duplicated (single-pass level) — the section's headline, over the same rows
 *  the table lists. */
export function tallyTones(rows: InventoryReconRow[]): Record<SituationTone, number> {
  const out: Record<SituationTone, number> = { agree: 0, differ: 0, dup: 0 };
  for (const r of rows) out[situationTone(r.situation)]++;
  return out;
}
